<template>
  <h5 class="mb-3">{{ name }}</h5>

  <div class="form-floating mb-4">
    <currency-input
      ref="uiAmount"
      description="amount"
      elementId="amount"
      @entered="onSaveShortcut"
    />
  </div>

  <p class="my-3 text-danger" v-show="showValidation">
    <i class="bi bi-x-circle-fill"></i>
    Amount not valid.
  </p>

  <div class="row">
    <month-and-year
      ref="payableFrom"
      monthLabel="received on month"
      yearLabel="year"
      ariaMonthLabel="Received on month"
      ariaYearLabel="Received on year"
      :mode="MonthYearDisplayMode.Year"
      :defaultYearRange="40"
    />
  </div>

  <bad-response-viewer ref="badResponseViewer" />

  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <button
      type="button"
      class="btn btn-link text-dark text-decoration-none me-md-2"
      aria-label="Close"
      v-on:click="onCancelClicked"
    >
      Cancel
    </button>

    <lock-button :text="buttonText" :async-action="onSaveClicked"></lock-button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide } from "vue";
import { inputValidator } from "@/db/ts/inputValidator";
import { OtherCash } from "@/guiide/ts/otherCash";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import CurrencyInput from "../../../shared/components/CurrencyInput.vue";
import MonthAndYear from "../common/MonthAndYear.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import BadResponseViewer from "../../../shared/components/BadResponseViewer.vue";
import { formatter } from "../../../db/ts/formatter";
import ResponseTracker from "@/shared/ts/responseTracker";
import { app } from "../../ts/app";
import { AxiosResponse } from "axios";
import MonthYearDisplayMode from "@/guiide/ts/components/MonthYearDisplayMode";

export default defineComponent({
  name: "OtherCashEditor",
  emits: ["edit-finished"],
  components: {
    CurrencyInput,
    BadResponseViewer,
    MonthAndYear,
    LockButton
  },
  setup() {
    const uiAmount = ref(null);
    const flagInvalidAnswer = ref(false);
    const payableFrom = ref(null);
    const badResponseViewer = ref(null);
    const execute = ref(false);

    provide('execute', execute);

    return {
      uiAmount,
      flagInvalidAnswer,
      payableFrom,
      badResponseViewer,
      execute,
      MonthYearDisplayMode
    };
  },
  data() {
    return {
      inputValidator,
      formatter,
      otherCash: null as OtherCash,
      name: "",
      responseTracker: ResponseTracker.empty<AxiosResponse<unknown>>()
    };
  },
  computed: {
    showValidation: function() {
      return this.flagInvalidAnswer;
    },
    buttonText: function(): string {
      return (this.otherCash?.id ?? Constants.NewId) === Constants.NewId
        ? "Add"
        : "Update";
    }
  },
  methods: {
    setValues(otherCash: OtherCash) {
      this.badResponseViewer.clear();
      this.otherCash = otherCash;
      this.name = otherCash.name;
      this.uiAmount.setValue(otherCash.amount == 0 ? null : otherCash.amount);
      this.payableFrom.setFirstYear(new Date().getFullYear());
      this.payableFrom.setSelectedValue(
        otherCash.payableFromMonth,
        otherCash.payableFromYear
      );
    },
    onCancelClicked: function() {
      this.editIsFinished();
    },
    onSaveShortcut: async function() {
      this.execute = true;
    },
    onSaveClicked: async function() {
      if (!this.uiAmount.validate()) {
        this.execute = false;
        return;
      }

      let receiptMonth = this.payableFrom.getSelectedMonth();
      const receiptYear = this.payableFrom.getSelectedYear();
      const currentYear = new Date().getFullYear();

      const retiree = this.$store.state.retiree;
      if (retiree.alreadyDrawingPension && receiptYear > currentYear) {
        const retirementDate = new Date(this.$store.state.retiree.retirementDate);
        receiptMonth = retirementDate.getMonth() + 1;
      }

      const update = ObjectBuilder.createOtherCash(
        this.otherCash.id,
        this.otherCash.name,
        this.uiAmount.getValue(),
        receiptMonth,
        receiptYear
      );

      const responseTracker = await app.updateOtherCash(update);
      this.execute = false;

      if (responseTracker.isOk) {
        this.editIsFinished();
      } else {
        if (responseTracker.trackerWillHandleFailure) {
          responseTracker.handleFailure();
        } else {
          this.badResponseViewer.update(
            responseTracker.isOk,
            responseTracker.message
          );
        }
      }
    },
    editIsFinished() {
      this.uiAmount.hideAnyWarnings();
      this.$emit("edit-finished");
    },
    getYearsPayableFrom() {
      const currentYear = new Date().getFullYear();
      const allYears = [];
      const lastYear = currentYear + 10;

      for (let i = currentYear; i <= lastYear; i++) {
        allYears.push(i);
      }

      return allYears;
    }
  }
});
</script>

<style>

</style>
